import React, { useRef } from 'react';
import { Button, colors, Modal } from '@insurancemenu/imenu-components-lib';
import FormSpinner from 'components/common/loaders/FormSpinner';
import { iconLayout, ModalType } from 'global/utils/ModalsHelper';
import clsx from 'clsx';
import { ModalPropsType } from '@insurancemenu/imenu-components-lib/dist/types';
import { createStyles } from './styles';
import { ACTION_MODAL_ACTION_BTN_TEST_ID, ACTION_MODAL_CLOSE_BTN_TEST_ID } from '../constants';

type ActionModalProps = Omit<ModalPropsType, 'children' | 'title'> & {
  title: string | JSX.Element;
  subtitle?: string | JSX.Element;
  modalContent?: JSX.Element;
  actionButtonText?: string;
  closeButtonText?: string;
  showCloseButton?: boolean;
  onAction?: () => void;
  modalType?: ModalType;
  icon?: JSX.Element;
  loading?: boolean;
  closable?: boolean;
  isValid?: boolean;
};

/**
 * @deprecated Use ActionModal from the library instead
 */
const ActionModal = ({
  show,
  title,
  subtitle,
  modalContent = <></>,
  actionButtonText,
  closeButtonText,
  showCloseButton = true,
  onClose,
  onAction,
  icon,
  modalType = ModalType.WARNING,
  loading = false,
  closable = true,
  isValid = true,
  className = '',
  ...rest
}: ActionModalProps): JSX.Element => {
  const classes = createStyles(!!subtitle);
  const scrollRef = useRef<HTMLDivElement>(null);

  const closeWithScrollUp = (): void => {
    onClose();
    scrollRef.current?.scrollTo(0, 0);
  };

  return (
    <Modal
      show={show}
      onClose={closeWithScrollUp}
      className={clsx(classes.modal, className)}
      closable={closable}
      modalContainerRef={scrollRef}
      {...rest}
    >
      {icon || iconLayout(modalType, classes.icon)}
      <p className={classes.modalTitle}>{title}</p>
      {subtitle && <div className={classes.contentText}>{subtitle}</div>}
      {modalContent}
      <footer className={classes.modalFooter}>
        {showCloseButton && (
          <Button
            type="outlined"
            onClick={closeWithScrollUp}
            size="large"
            data-test-id={ACTION_MODAL_CLOSE_BTN_TEST_ID}
          >
            {closeButtonText || 'Close'}
          </Button>
        )}
        {onAction && (
          <Button
            className={classes.actionButton}
            color={modalType === ModalType.DELETE ? 'custom' : 'primary'}
            customColor={modalType === ModalType.DELETE ? colors.red[600] : colors.blue[600]}
            onClick={onAction}
            size="large"
            disabled={loading || !isValid}
            data-test-id={ACTION_MODAL_ACTION_BTN_TEST_ID}
          >
            {actionButtonText || 'Save'}
          </Button>
        )}
      </footer>
      <FormSpinner display={loading} />
    </Modal>
  );
};

export default ActionModal;
